import { z } from "zod";

export const BRAND_STRATEGY_STATUSES = ["processing", "ready", "approved", "failed"] as const;
export type BrandStrategyStatus = (typeof BRAND_STRATEGY_STATUSES)[number];
export const brandStrategyStatusSchema = z.enum(BRAND_STRATEGY_STATUSES);

export const BRAND_STRATEGY_REASONING_STATES = ["fresh", "stale", "processing", "failed"] as const;
export type BrandStrategyReasoningState = (typeof BRAND_STRATEGY_REASONING_STATES)[number];
export const brandStrategyReasoningStateSchema = z.enum(BRAND_STRATEGY_REASONING_STATES);

export const CONTENT_PILLAR_KEYS = ["awareness", "education", "trust", "conversion"] as const;
export type ContentPillarKey = (typeof CONTENT_PILLAR_KEYS)[number];
export const contentPillarKeySchema = z.enum(CONTENT_PILLAR_KEYS);

export const CONTENT_PILLAR_METADATA: Record<
  ContentPillarKey,
  { label: string; description: string }
> = {
  awareness: {
    label: "Awareness",
    description: "Posts that help more people discover and recognize the brand.",
  },
  education: {
    label: "Education",
    description: "Posts that explain how the product works and why it matters.",
  },
  trust: {
    label: "Trust",
    description: "Posts that reduce doubt through proof, credibility, and reassurance.",
  },
  conversion: {
    label: "Conversion",
    description: "Posts that push toward sign-up, purchase, or another direct action.",
  },
};

export const CAMPAIGN_MIX_KEYS = ["attention", "evergreen", "seasonal", "conversion"] as const;
export type CampaignMixKey = (typeof CAMPAIGN_MIX_KEYS)[number];
export const campaignMixKeySchema = z.enum(CAMPAIGN_MIX_KEYS);

export const CAMPAIGN_MIX_METADATA: Record<CampaignMixKey, { label: string; description: string }> =
  {
    attention: {
      label: "Attention",
      description: "Campaigns built to attract interest and broaden reach.",
    },
    evergreen: {
      label: "Evergreen",
      description: "Campaigns that stay useful over time and can run continuously.",
    },
    conversion: {
      label: "Conversion",
      description: "Campaigns focused on turning interest into action or sales.",
    },
    seasonal: {
      label: "Seasonal",
      description: "Campaigns tied to timely moments or calendar-driven opportunities.",
    },
  };

const weightedKeySchema = <T extends z.ZodTypeAny>(keySchema: T) =>
  z.object({
    key: keySchema,
    percentage: z.number().int().min(0).max(100),
  });

export const brandStrategyReasoningV1Schema = z.object({
  version: z.literal(2),
  summary: z.string().trim().min(1),
  bulletsMarkdown: z.string().trim().min(1),
});
export type BrandStrategyReasoningV1 = z.infer<typeof brandStrategyReasoningV1Schema>;

export const contentPillarAllocationSchema = weightedKeySchema(contentPillarKeySchema);
export type ContentPillarAllocation = z.infer<typeof contentPillarAllocationSchema>;

export const campaignMixAllocationSchema = weightedKeySchema(campaignMixKeySchema);
export type CampaignMixAllocation = z.infer<typeof campaignMixAllocationSchema>;

const validateUniqueKeys = <T extends { key: string }>(
  values: T[],
  ctx: z.RefinementCtx,
  path: string,
) => {
  const seen = new Set<string>();

  for (const [index, value] of values.entries()) {
    if (seen.has(value.key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate key '${value.key}' is not allowed`,
        path: [path, index, "key"],
      });
      continue;
    }

    seen.add(value.key);
  }
};

const validatePercentages = <T extends { percentage: number }>(
  values: T[],
  ctx: z.RefinementCtx,
  path: string,
) => {
  const total = values.reduce((sum, value) => sum + value.percentage, 0);
  if (total !== 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Percentages must add up to 100",
      path: [path],
    });
  }
};

export const brandStrategyInputSchema = z
  .object({
    postsPerDay: z.number().int().min(1),
    contentPillars: z.array(contentPillarAllocationSchema).min(1),
    campaignMix: z.array(campaignMixAllocationSchema).min(1),
  })
  .superRefine((value, ctx) => {
    validateUniqueKeys(value.contentPillars, ctx, "contentPillars");
    validateUniqueKeys(value.campaignMix, ctx, "campaignMix");
    validatePercentages(value.contentPillars, ctx, "contentPillars");
    validatePercentages(value.campaignMix, ctx, "campaignMix");
  });

export const brandStrategySchema = brandStrategyInputSchema.extend({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  status: brandStrategyStatusSchema,
  reasoningState: brandStrategyReasoningStateSchema,
  isApproved: z.boolean(),
  approvedAt: z.string().datetime().nullable(),
  approvedByUserId: z.string().nullable(),
  reasoning: brandStrategyReasoningV1Schema.nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type BrandStrategy = z.infer<typeof brandStrategySchema>;

export type BrandStrategyUpdateInput = z.infer<typeof brandStrategyInputSchema>;

export interface BrandStrategyValidationResult {
  isValid: boolean;
  validationIssues: string[];
}

export const validateBrandStrategyInput = (
  input: BrandStrategyUpdateInput,
): BrandStrategyValidationResult => {
  const parsed = brandStrategyInputSchema.safeParse(input);
  if (parsed.success) {
    return { isValid: true, validationIssues: [] };
  }

  return {
    isValid: false,
    validationIssues: parsed.error.issues.map((issue) => issue.message),
  };
};
